import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { TimePicker } from '../../components/ui/time-picker';
import { ArrowLeft, RefreshCw, AlertTriangle } from 'lucide-react';
import { useRestaurant } from '../../context/RestaurantContext';
import { useAppSettings } from '../../context/AppSettingsContext';
import { reportByKey } from './reportsCatalog';
import { CRM_REPORT_BODIES } from './crmReportBodies';

const iso = (d) => d.toISOString().slice(0, 10);
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d; };

function Kpi({ label, value, sub }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-foreground">{value}</div>
      {sub != null && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function Bar({ label, value, max, display }) {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <div className="flex items-center gap-2 text-sm">
      <div className="w-28 shrink-0 truncate text-muted-foreground" title={label}>{label}</div>
      <div className="flex-1 bg-secondary rounded-full h-4 overflow-hidden">
        <div className="h-4 bg-primary/70 rounded-full" style={{ width: `${pct}%` }} />
      </div>
      <div className="w-24 shrink-0 text-right font-medium">{display}</div>
    </div>
  );
}

export default function ReportView() {
  const { reportKey } = useParams();
  const navigate = useNavigate();
  const report = reportByKey(reportKey);
  const { restaurantId, locationId, location } = useRestaurant();
  const { formatCurrency, formatDate } = useAppSettings();
  const money = (n) => (formatCurrency ? formatCurrency(n || 0) : `${(n || 0).toFixed(2)}`);
  const dt = (isoStr) => (isoStr ? (formatDate ? formatDate(isoStr) : isoStr.slice(0, 10)) : '—');

  const [dateFrom, setDateFrom] = useState(iso(daysAgo(29)));
  const [dateTo, setDateTo] = useState(iso(new Date()));
  const [timeFrom, setTimeFrom] = useState('00:00');
  const [timeTo, setTimeTo] = useState('23:59');
  const [scopeLocation, setScopeLocation] = useState(true);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    if (!restaurantId || !report) return;
    setLoading(true);
    try {
      const d = await report.fetch(restaurantId, {
        dateFrom, dateTo, timeFrom, timeTo,
        locationId: scopeLocation && locationId ? locationId : undefined,
      });
      setData(d);
    } finally {
      setLoading(false);
    }
  }, [restaurantId, report, dateFrom, dateTo, timeFrom, timeTo, scopeLocation, locationId]);

  useEffect(() => { load(); }, [load]);

  if (!report) {
    return (
      <div className="p-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/reports')}>
          <ArrowLeft className="h-4 w-4 mr-2" /> All reports
        </Button>
        <p className="mt-4 text-muted-foreground">Unknown report.</p>
      </div>
    );
  }

  return (
    <div className="p-6 w-full space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/reports')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">{report.title}</h1>
        </div>
      </div>

      {/* The date and time filter, hidden for lifetime reports the backend scopes by the staff JWT. */}
      {!report.noFilters && (
      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-card p-3">
        <label className="text-xs text-muted-foreground">From date
          <Input type="date" value={dateFrom} max={dateTo} onChange={(e) => setDateFrom(e.target.value)} className="h-9 mt-1" />
        </label>
        <label className="text-xs text-muted-foreground">From time
          <TimePicker value={timeFrom} onChange={setTimeFrom} step={15} className="mt-1" />
        </label>
        <label className="text-xs text-muted-foreground">To date
          <Input type="date" value={dateTo} min={dateFrom} onChange={(e) => setDateTo(e.target.value)} className="h-9 mt-1" />
        </label>
        <label className="text-xs text-muted-foreground">To time
          <TimePicker value={timeTo} onChange={setTimeTo} step={15} className="mt-1" />
        </label>
        {locationId && (
          <button
            type="button"
            onClick={() => setScopeLocation((v) => !v)}
            className={`h-9 px-3 rounded-md border text-sm ${scopeLocation ? 'border-primary bg-accent text-accent-foreground' : 'border-border bg-card text-muted-foreground'}`}
            title="Toggle between this branch and all branches"
          >
            {scopeLocation ? (location?.name || 'This branch') : 'All branches'}
          </button>
        )}
        <Button variant="outline" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Apply
        </Button>
      </div>
      )}

      {/* Body */}
      {loading && !data ? (
        <p className="text-sm text-muted-foreground py-8 text-center">Loading…</p>
      ) : (
        <ReportBody
          reportKey={report.key}
          data={data}
          money={money}
          dt={dt}
          openProfile={(customerId) => navigate(`/crm?customer=${customerId}`)}
        />
      )}
    </div>
  );
}

function ReportBody({ reportKey, data, money, dt, openProfile }) {
  if (!data) return <p className="text-sm text-muted-foreground py-8 text-center">No data.</p>;

  // CRM guest reports map each key to a body component, with guest rows linking to Customer 360.
  const CrmBody = CRM_REPORT_BODIES[reportKey];
  if (CrmBody) return <CrmBody data={data} money={money} dt={dt} openProfile={openProfile} />;

  if (reportKey === 'buffet') {
    const t = data.totals || {};
    const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Kpi label="Covers" value={t.covers ?? 0} sub={`${t.adults ?? 0} adults · ${t.children ?? 0} children`} />
          <Kpi label="Revenue" value={money(t.revenue)} sub={`${t.sales ?? 0} sales`} />
          <Kpi label="ARPC" value={money(t.arpc)} />
          <Kpi label="Covers / day" value={(t.covers && data.days) ? (t.covers / data.days).toFixed(1) : '0'} sub={`${data.days ?? 0} days`} />
          <Kpi label="Waste cost" value={money(t.waste_cost)} sub={`${t.waste_entries ?? 0} log entries`} />
        </div>

        <Card>
          <CardHeader><CardTitle className="text-base">Per-buffet economics</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-border">
                  <th className="px-3 py-2">Buffet</th><th className="px-3 py-2">Covers</th>
                  <th className="px-3 py-2">Avg/day</th><th className="px-3 py-2">Revenue</th>
                  <th className="px-3 py-2">ARPC</th><th className="px-3 py-2">Food cost %</th>
                  <th className="px-3 py-2">Break-even covers/day</th><th className="px-3 py-2">Waste</th>
                  <th className="px-3 py-2">Est. profit</th>
                </tr>
              </thead>
              <tbody>
                {(data.by_buffet || []).map((b) => (
                  <tr key={b.buffet_id} className="border-b border-border/60 last:border-0">
                    <td className="px-3 py-2 font-medium">{b.name}</td>
                    <td className="px-3 py-2">{b.covers}</td>
                    <td className="px-3 py-2">{b.avg_covers_per_day ?? '—'}</td>
                    <td className="px-3 py-2">{money(b.revenue)}</td>
                    <td className="px-3 py-2">{money(b.arpc)}</td>
                    <td className="px-3 py-2">{b.food_cost_pct != null ? `${b.food_cost_pct}%` : '—'}</td>
                    <td className="px-3 py-2">{b.break_even_covers_per_day ?? '—'}</td>
                    <td className="px-3 py-2">{money(b.waste_cost)}</td>
                    <td className={`px-3 py-2 font-medium ${b.est_profit != null ? (b.est_profit >= 0 ? 'text-emerald-700' : 'text-red-600') : ''}`}>
                      {b.est_profit != null ? money(b.est_profit) : '—'}
                    </td>
                  </tr>
                ))}
                {(data.by_buffet || []).length === 0 && (
                  <tr><td colSpan={9} className="px-3 py-6 text-center text-muted-foreground">No buffet sales in this range.</td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Covers by weekday</CardTitle></CardHeader>
            <CardContent className="space-y-1.5">
              {(data.by_weekday || []).map((r) => (
                <Bar key={r.day} label={DAYS[r.day]} value={r.covers}
                  max={Math.max(1, ...(data.by_weekday || []).map((x) => x.covers))}
                  display={r.covers} />
              ))}
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Throughput by hour</CardTitle></CardHeader>
            <CardContent className="space-y-1.5">
              {(data.by_hour || []).length === 0 && <p className="text-xs text-muted-foreground italic">No sales in range.</p>}
              {(data.by_hour || []).map((r) => (
                <Bar key={r.hour} label={`${String(r.hour).padStart(2, '0')}:00`} value={r.covers}
                  max={Math.max(1, ...(data.by_hour || []).map((x) => x.covers))}
                  display={r.covers} />
              ))}
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Waste by reason</CardTitle></CardHeader>
            <CardContent>
              {(data.waste_by_reason || []).length === 0 ? (
                <p className="text-xs text-muted-foreground italic">No waste logged in range.</p>
              ) : (
                <ul className="space-y-1.5 text-xs">
                  {data.waste_by_reason.map((r) => (
                    <li key={r.reason} className="flex justify-between">
                      <span>{r.reason}{r.entries != null ? ` · ${r.entries} entries` : ''}</span>
                      <span className="font-medium">{money(r.est_cost)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (reportKey === 'sales') {
    const maxDaily = Math.max(1, ...((data.daily || []).map((d) => d.revenue)));
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Kpi label="Revenue" value={money(data.total_revenue)} sub={`${data.order_count} orders`} />
          <Kpi label="Avg order value" value={money(data.avg_order_value)} />
          <Kpi label="Orders" value={data.order_count} />
        </div>
        <Card>
          <CardHeader><CardTitle className="text-lg">Daily revenue</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {(data.daily || []).length === 0
              ? <p className="text-sm text-muted-foreground py-4 text-center">No sales in this window.</p>
              : data.daily.map((d) => <Bar key={d.date} label={d.date} value={d.revenue} max={maxDaily} display={money(d.revenue)} />)}
            {(data.by_location || []).length > 1 && (
              <div className="pt-3 mt-2 border-t border-border">
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">By branch</p>
                {data.by_location.map((l) => (
                  <div key={l.location_id} className="flex justify-between text-sm py-0.5">
                    <span className="text-muted-foreground">{l.location}</span>
                    <span className="font-medium">{money(l.revenue)} · {l.orders} orders</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (reportKey === 'menu') {
    const maxRev = Math.max(1, ...((data.top_by_revenue || []).map((d) => d.revenue)));
    const maxUnits = Math.max(1, ...((data.top_by_units || []).map((d) => d.units)));
    return (
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="text-lg">Top dishes — revenue</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {(data.top_by_revenue || []).length === 0
              ? <p className="text-sm text-muted-foreground py-4 text-center">No items sold.</p>
              : data.top_by_revenue.map((d) => <Bar key={`r-${d.dish_id}-${d.name}`} label={d.name} value={d.revenue} max={maxRev} display={money(d.revenue)} />)}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-lg">Top dishes — units</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {(data.top_by_units || []).length === 0
              ? <p className="text-sm text-muted-foreground py-4 text-center">No items sold.</p>
              : data.top_by_units.map((d) => <Bar key={`u-${d.dish_id}-${d.name}`} label={d.name} value={d.units} max={maxUnits} display={`${d.units}`} />)}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (reportKey === 'food-cost') {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Food sales" value={money(data.food_sales)} />
          <Kpi label="Food cost" value={money(data.total_food_cost)} sub={data.food_cost_pct != null ? `${data.food_cost_pct}%` : null} />
          <Kpi label="Gross margin" value={data.gross_margin_pct != null ? `${data.gross_margin_pct}%` : '—'} />
          <Kpi label="Liquor pour cost" value={data.liquor_available ? money(data.liquor_pour_cost) : '—'} sub={!data.liquor_available ? 'costing not enabled' : null} />
        </div>
        {(data.ingredients || []).length > 0 && (
          <Card>
            <CardHeader><CardTitle className="text-lg">Ingredient consumption</CardTitle></CardHeader>
            <CardContent>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted-foreground border-b border-border">
                    <th className="py-1 font-medium">Ingredient</th>
                    <th className="py-1 font-medium text-right">Used</th>
                    <th className="py-1 font-medium text-right">Unit cost</th>
                    <th className="py-1 font-medium text-right">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {data.ingredients.map((i) => (
                    <tr key={i.ingredient_id} className="border-b border-border/50">
                      <td className="py-1">{i.name}</td>
                      <td className="py-1 text-right">{i.used} {i.unit}</td>
                      <td className="py-1 text-right">{i.unit_cost != null ? money(i.unit_cost) : '—'}</td>
                      <td className="py-1 text-right font-medium">{i.cost != null ? money(i.cost) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
        {(data.low_stock || []).length > 0 && (
          <div className="rounded-md border border-amber-300 bg-amber-50/50 p-3">
            <p className="flex items-center gap-1.5 text-sm font-medium text-amber-800 mb-1">
              <AlertTriangle className="h-4 w-4" /> Low liquor stock
            </p>
            {data.low_stock.map((s) => (
              <div key={`${s.dish_id}-${s.location}`} className="flex justify-between text-sm text-amber-900">
                <span>{s.name} <span className="text-amber-700">· {s.location}</span></span>
                <span>{s.full_bottles} / {s.threshold} bottles</span>
              </div>
            ))}
          </div>
        )}
        {data.note && <p className="text-[11px] text-muted-foreground">{data.note}</p>}
      </div>
    );
  }

  if (reportKey === 'service') {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Cancellation rate" value={`${data.cancellation_rate_pct ?? 0}%`} sub={`${data.cancelled_orders ?? 0} of ${data.total_orders ?? 0}`} />
          <Kpi label="Void rate" value={`${data.void_rate_pct ?? 0}%`} sub={`${data.voided_orders ?? 0} voided`} />
          <Kpi label="Avg fulfilment" value={data.avg_fulfilment_minutes != null ? `${data.avg_fulfilment_minutes}m` : '—'} />
          <Kpi label="Avg turnaround" value={data.avg_table_turnaround_minutes != null ? `${data.avg_table_turnaround_minutes}m` : '—'} />
          <Kpi label="Avg party size" value={data.avg_party_size ?? '—'} />
          <Kpi label="Covers" value={data.covers ?? 0} sub={`${data.closed_sessions ?? 0} sessions`} />
        </div>
        {data.note && <p className="text-[11px] text-muted-foreground">{data.note}</p>}
      </div>
    );
  }

  return <p className="text-sm text-muted-foreground">Unknown report.</p>;
}
